import { useEffect, useState } from "react";
import { Cloud, CloudOff, RefreshCw } from "lucide-react";
import { pendingCount, subscribe, drainQueue } from "@/lib/offlineQueue";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function OfflineIndicator() {
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [pending, setPending] = useState(0);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    setPending(pendingCount());
    const unsub = subscribe(setPending);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      unsub();
    };
  }, []);

  // When we go online and there are pending items, drain.
  useEffect(() => {
    let cancelled = false;
    if (online && pending > 0 && !syncing) {
      setSyncing(true);
      drainQueue(api).then(({ drained, remaining }) => {
        if (cancelled) return;
        setSyncing(false);
        if (drained > 0) {
          toast.success(`Synced ${drained} queued action${drained === 1 ? "" : "s"}`);
          // Notify pages to refresh their match data
          window.dispatchEvent(new CustomEvent("crickpad-queue-drained"));
        }
        if (remaining > 0) {
          toast.error(`${remaining} action${remaining === 1 ? "" : "s"} still pending`);
        }
      });
    }
    return () => { cancelled = true; };
  }, [online, pending, syncing]);

  if (online && pending === 0 && !syncing) return null;

  let label = "Offline";
  let Icon = CloudOff;
  let cls = "border-destructive text-destructive";

  if (syncing) {
    label = `Syncing ${pending}…`;
    Icon = RefreshCw;
    cls = "border-primary text-primary";
  } else if (online && pending > 0) {
    label = `Pending ${pending}`;
    Icon = Cloud;
    cls = "border-primary text-primary";
  } else if (!online && pending > 0) {
    label = `Offline · ${pending} queued`;
  }

  return (
    <div
      data-testid="offline-indicator"
      className={`hidden sm:inline-flex items-center gap-1.5 px-2 py-1 border ${cls} text-[10px] uppercase tracking-[0.2em] font-bold`}
      style={{ borderRadius: 2 }}
    >
      <Icon className={`w-3 h-3 ${syncing ? "animate-spin" : ""}`} strokeWidth={3} />
      <span>{label}</span>
    </div>
  );
}
